import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, MapPin, Settings, X, Save, Building, Users } from 'lucide-react';
import { useAlert } from './contexts/AlertContext';
import { useAuth } from './contexts/AuthContext';
import apiService from './apiService';

const LocationManagement = () => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [formData, setFormData] = useState({
        type: 'consultation',
        name: '',
        shortName: '',
        roomNumber: '',
        floor: '',
        wing: '',
        building: 'Main Building',
        department: '',
        capacity: 1,
        icon: '🏥',
        colorCode: '#3B82F6',
        equipment: [],
        specialNotes: ''
    });
    const [newEquipment, setNewEquipment] = useState('');

    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async () => {
        setLoading(true);
        try {
            const data = await apiService.getLocations();
            setLocations(data);
        } catch (error) {
            console.error('Error fetching locations:', error);
            showAlert('Failed to load locations', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingLocation) {
                await apiService.updateLocation(editingLocation.id, formData);
                showAlert('✅ Location updated successfully', 'success');
            } else {
                await apiService.createLocation(formData);
                showAlert('✅ Location created successfully', 'success');
            }
            
            setShowModal(false);
            resetForm();
            fetchLocations();
        } catch (error) {
            console.error('Error saving location:', error);
            showAlert(`Failed to save location: ${error.message}`, 'error');
        }
    };

    const handleEdit = (location) => {
        setEditingLocation(location);
        setFormData({
            type: location.type,
            name: location.name,
            shortName: location.shortName || '',
            roomNumber: location.roomNumber || '',
            floor: location.floor || '',
            wing: location.wing || '',
            building: location.building || 'Main Building',
            department: location.department || '',
            capacity: location.capacity || 1,
            icon: location.icon || '🏥',
            colorCode: location.colorCode || '#3B82F6',
            equipment: location.equipment || [],
            specialNotes: location.specialNotes || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (locationId) => {
        if (!window.confirm('Are you sure you want to delete this location?')) {
            return;
        }
        
        try {
            await apiService.deleteLocation(locationId);
            showAlert('✅ Location deleted successfully', 'success');
            fetchLocations();
        } catch (error) {
            console.error('Error deleting location:', error);
            showAlert(`Failed to delete location: ${error.message}`, 'error');
        }
    };

    const resetForm = () => {
        setEditingLocation(null);
        setFormData({
            type: 'consultation',
            name: '',
            shortName: '',
            roomNumber: '',
            floor: '',
            wing: '',
            building: 'Main Building',
            department: '',
            capacity: 1,
            icon: '🏥',
            colorCode: '#3B82F6',
            equipment: [],
            specialNotes: ''
        });
        setNewEquipment('');
    };

    const addEquipment = () => {
        if (newEquipment.trim()) {
            setFormData(prev => ({
                ...prev,
                equipment: [...prev.equipment, newEquipment.trim()]
            }));
            setNewEquipment('');
        }
    };

    const removeEquipment = (index) => {
        setFormData(prev => ({
            ...prev,
            equipment: prev.equipment.filter((_, i) => i !== index)
        }));
    };

    const groupLocationsByType = () => {
        const grouped = {};
        locations.forEach(loc => {
            if (!grouped[loc.type]) {
                grouped[loc.type] = [];
            }
            grouped[loc.type].push(loc);
        });
        return grouped;
    };

    const getTypeLabel = (type) => {
        const labels = {
            'triage': 'Triage Rooms',
            'consultation': 'Consultation Rooms',
            'lab': 'Laboratory',
            'imaging': 'Imaging/Radiology',
            'pharmacy': 'Pharmacy',
            'procedure': 'Procedure Rooms',
            'emergency': 'Emergency',
            'surgery': 'Surgery/Operating Theater',
            'ward': 'Wards/Admission',
            'billing': 'Billing Counters'
        };
        return labels[type] || type;
    };

    const getTypeIcon = (type) => {
        const icons = {
            'triage': '🏥',
            'consultation': '👨‍⚕️',
            'lab': '🔬',
            'imaging': '📷',
            'pharmacy': '💊',
            'procedure': '🏥',
            'emergency': '🚨',
            'surgery': '⚕️',
            'ward': '🛏️',
            'billing': '💰'
        };
        return icons[type] || '📍';
    };

    const groupedLocations = groupLocationsByType();

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Location & Room Management</h2>
                    <p className="text-gray-600">Manage hospital rooms and service locations</p>
                </div>
                <button
                    onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
                >
                    <Plus className="w-4 h-4" />
                    Add New Location
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Building className="w-6 h-6 text-blue-500" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Total Locations</p>
                            <p className="text-xl font-bold text-gray-900">{locations.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Users className="w-6 h-6 text-green-500" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Active Rooms</p>
                            <p className="text-xl font-bold text-gray-900">{locations.filter(l => l.isActive).length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <MapPin className="w-6 h-6 text-purple-500" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Location Types</p>
                            <p className="text-xl font-bold text-gray-900">{Object.keys(groupedLocations).length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Settings className="w-6 h-6 text-orange-500" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Maintenance</p>
                            <p className="text-xl font-bold text-gray-900">{locations.filter(l => l.isUnderMaintenance).length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Locations List by Type */}
            {loading ? (
                <div className="text-center py-12">
                    <p className="text-gray-500">Loading locations...</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {Object.entries(groupedLocations).map(([type, locs]) => (
                        <div key={type} className="bg-white rounded-lg shadow-sm border">
                            <div className="p-4 border-b bg-gray-50">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <span className="text-2xl">{getTypeIcon(type)}</span>
                                    {getTypeLabel(type)} ({locs.length})
                                </h3>
                            </div>
                            <div className="p-4">
                                <div className="space-y-3">
                                    {locs.map((location) => (
                                        <div key={location.id} className="border rounded-lg p-4 hover:bg-gray-50">
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <h4 className="font-semibold text-gray-900">{location.name}</h4>
                                                        {location.shortName && (
                                                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                                                {location.shortName}
                                                            </span>
                                                        )}
                                                        {!location.isActive && (
                                                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                                                                Inactive
                                                            </span>
                                                        )}
                                                        {location.isUnderMaintenance && (
                                                            <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                                                                Maintenance
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                                        <MapPin className="w-3 h-3" />
                                                        {location.fullAddress || `${location.floor || ''} ${location.wing || ''}`.trim() || 'No address'}
                                                    </p>
                                                    {location.department && (
                                                        <p className="text-xs text-gray-500 mt-1">Department: {location.department}</p>
                                                    )}
                                                    {location.assignedStaffId && (
                                                        <p className="text-xs text-gray-500">Assigned to staff ID: {location.assignedStaffId}</p>
                                                    )}
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Capacity: {location.capacity} • Display Order: {location.displayOrder || 'N/A'}
                                                    </p>
                                                    {location.equipment && location.equipment.length > 0 && (
                                                        <div className="mt-2">
                                                            <p className="text-xs text-gray-500">Equipment:</p>
                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                {location.equipment.map((eq, idx) => (
                                                                    <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                                                                        {eq}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleEdit(location)}
                                                        className="text-blue-600 hover:text-blue-800 p-2"
                                                        title="Edit"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(location.id)}
                                                        className="text-red-600 hover:text-red-800 p-2"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {locations.length === 0 && !loading && (
                        <div className="text-center py-12 bg-white rounded-lg border">
                            <Building className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                            <p className="text-gray-500 mb-4">No locations created yet</p>
                            <button
                                onClick={() => setShowModal(true)}
                                className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                                Create your first location
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Add/Edit Location Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {editingLocation ? 'Edit Location' : 'Create New Location'}
                            </h3>
                            <button onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {/* Location Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Location Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => setFormData({...formData, type: e.target.value, icon: getTypeIcon(e.target.value)})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    required
                                >
                                    <option value="triage">Triage Room</option>
                                    <option value="consultation">Consultation Room</option>
                                    <option value="lab">Laboratory</option>
                                    <option value="imaging">Imaging/Radiology</option>
                                    <option value="pharmacy">Pharmacy</option>
                                    <option value="procedure">Procedure Room</option>
                                    <option value="emergency">Emergency Room</option>
                                    <option value="surgery">Surgery/Operating Theater</option>
                                    <option value="ward">Ward/Admission</option>
                                    <option value="billing">Billing Counter</option>
                                </select>
                            </div>

                            {/* Room Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Room Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    placeholder="e.g., Consultation Room 3"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            {/* Short Name & Room Number */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Short Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.shortName}
                                        onChange={(e) => setFormData({...formData, shortName: e.target.value})}
                                        placeholder="e.g., C3 or Room 3"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Room Number
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.roomNumber}
                                        onChange={(e) => setFormData({...formData, roomNumber: e.target.value})}
                                        placeholder="e.g., 3"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Physical Location */}
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Floor</label>
                                    <input
                                        type="text"
                                        value={formData.floor}
                                        onChange={(e) => setFormData({...formData, floor: e.target.value})}
                                        placeholder="e.g., 2nd Floor"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Wing</label>
                                    <input
                                        type="text"
                                        value={formData.wing}
                                        onChange={(e) => setFormData({...formData, wing: e.target.value})}
                                        placeholder="e.g., East Wing"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Building</label>
                                    <input
                                        type="text"
                                        value={formData.building}
                                        onChange={(e) => setFormData({...formData, building: e.target.value})}
                                        placeholder="Main Building"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Department & Capacity */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Department (Optional)</label>
                                    <input
                                        type="text"
                                        value={formData.department}
                                        onChange={(e) => setFormData({...formData, department: e.target.value})}
                                        placeholder="e.g., Cardiology"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Capacity</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={formData.capacity}
                                        onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Equipment */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Equipment Available</label>
                                <div className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={newEquipment}
                                        onChange={(e) => setNewEquipment(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEquipment())}
                                        placeholder="e.g., ECG Machine"
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={addEquipment}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium"
                                    >
                                        Add
                                    </button>
                                </div>
                                {formData.equipment.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {formData.equipment.map((eq, idx) => (
                                            <div key={idx} className="px-3 py-1 bg-blue-50 text-blue-800 rounded-full text-xs flex items-center gap-1">
                                                {eq}
                                                <button
                                                    type="button"
                                                    onClick={() => removeEquipment(idx)}
                                                    className="hover:text-blue-900"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Display Settings */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Icon (Emoji)</label>
                                    <input
                                        type="text"
                                        value={formData.icon}
                                        onChange={(e) => setFormData({...formData, icon: e.target.value})}
                                        placeholder="🏥"
                                        maxLength={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-2xl text-center"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Color Code</label>
                                    <input
                                        type="color"
                                        value={formData.colorCode}
                                        onChange={(e) => setFormData({...formData, colorCode: e.target.value})}
                                        className="w-full h-10 border border-gray-300 rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Special Notes */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Special Notes</label>
                                <textarea
                                    value={formData.specialNotes}
                                    onChange={(e) => setFormData({...formData, specialNotes: e.target.value})}
                                    rows={2}
                                    placeholder="Any special notes about this location..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); resetForm(); }}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
                                >
                                    <Save className="w-4 h-4" />
                                    {editingLocation ? 'Update Location' : 'Create Location'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocationManagement;

